import { isFocusedInboxEnabled } from 'owa-mail-list-actions/lib/utils/FocusedInboxUtil';
import { logInboxPlusExperimentData } from 'owa-mail-inbox-plus-store';
import { createLazyOrchestrator } from 'owa-bundling';
import { lazyLogFloodgateActivity, SurveyActivityType } from 'owa-floodgate-feedback-view';
import { logUsage } from 'owa-analytics';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import { findItemWithStartIndex } from 'owa-mail-store';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import { createMailFolderTableQuery, getMailItemSortByProperty } from 'owa-mail-list-actions';
import InboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/store/schema/InboxClassificationFilter';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { MailFolderTableQuery, getBaseFolderId, getBaseServerFolderId } from 'owa-mail-list-store';
import getHashtagsToExclude from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagsToExclude';
import { getMailboxInfoFromTableQuery } from 'owa-mail-mailboxinfo';
import FindItemResponseMessage from 'owa-service/lib/contract/FindItemResponseMessage';
import getInboxPlusPivotDisplayProperties from 'owa-mail-inbox-plus-store/lib/selectors/getInboxPlusPivotDisplayProperties';
import { getTableViewFromTableQuery } from 'owa-mail-table-loading-actions';
import Folder from 'owa-service/lib/contract/Folder';
import FolderInfoResponseMessage from 'owa-service/lib/contract/FolderInfoResponseMessage';
import FolderResponseShape from 'owa-service/lib/contract/FolderResponseShape';
import folderId from 'owa-service/lib/factory/folderId';
import folderResponseShape from 'owa-service/lib/factory/folderResponseShape';
import getFolderRequest from 'owa-service/lib/factory/getFolderRequest';
import propertyUri from 'owa-service/lib/factory/propertyUri';
import getFolderOperation from 'owa-service/lib/operation/getFolderOperation';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import folderStore from 'owa-folders';
import getHashtagForInboxClassificationFilter from 'owa-mail-inbox-plus-store/lib/selectors/getHashtagForInboxClassificationFilter';
import FolderId from 'owa-service/lib/contract/FolderId';
import GetFolderJsonResponse from 'owa-service/lib/contract/GetFolderJsonResponse';
import { listViewStore } from 'owa-mail-list-store';
import { trace } from 'owa-trace';
import { isInEasyOnExperiment } from 'owa-inbox-plus-util/lib/util/inboxPlusFlightUtil';

const SERVICE_CALL_THROTTLE = 2000;

export const logIsFocusedOtherUserOrchestrator = createLazyOrchestrator(
    logInboxPlusExperimentData,
    'clone_logInboxPlusExperimentData',
    async actionMessage => {
        const logFloodgateActivity = await lazyLogFloodgateActivity.import();

        const floodgateCampaignName: SurveyActivityType = isInEasyOnExperiment()
            ? 'InboxPlusEasyOnExperimentSession'
            : 'InboxPlusExperimentSession';

        logFloodgateActivity(floodgateCampaignName);

        if (isFocusedInboxEnabled()) {
            logUsage('InboxPlusDefaultTabs_FocusedOtherUser', null, {
                isCore: true,
            });
        } else {
            logUsage('InboxPlusDefaultTabs_InboxUser', null, {
                isCore: true,
            });
        }
    }
);

export const logTabContentsSizeOrchestrator = createLazyOrchestrator(
    logInboxPlusExperimentData,
    'clone_logInboxPlusExperimentData',
    async actionMessage => {
        const pivotsToLog = [
            InboxClassificationFilter.Focused,
            InboxClassificationFilter.Promotions,
            InboxClassificationFilter.Social,
            InboxClassificationFilter.Newsletters,
            InboxClassificationFilter.Other,
        ];
        let getSearchFolderIdPromises = [];
        const inboxFolder = folderStore.folderTable.get(folderNameToId('inbox'));

        logUsage('TotalItemsInAll', [inboxFolder.TotalCount, inboxFolder.UnreadCount], {
            isCore: true,
        });

        // Get searchFolderIds for all pivots, we may have to issue findItem requests
        for (let i = 0; i < pivotsToLog.length; i++) {
            getSearchFolderIdPromises.push(
                getInboxPlusPivotSearchFolderId(pivotsToLog[i], i * SERVICE_CALL_THROTTLE)
            );
        }

        // Wait for all potential findItem calls to complete before we log data
        Promise.all(getSearchFolderIdPromises).then(searchFolderIds => {
            if (searchFolderIds.indexOf(null) === -1) {
                getSearchFoldersAndLogCounts(searchFolderIds, pivotsToLog);
            } else {
                trace.warn('Null searchfolderId for inbox plus pivot');
            }
        });
    }
);

async function getInboxPlusPivotSearchFolderId(
    pivot: InboxClassificationFilter,
    throttleTimeout: number
): Promise<string> {
    let focusedViewFilter;
    switch (pivot) {
        case InboxClassificationFilter.Focused:
            focusedViewFilter = FocusedViewFilter.Focused;
            break;
        case InboxClassificationFilter.Other:
            focusedViewFilter = FocusedViewFilter.Other;
            break;
        default:
            focusedViewFilter = FocusedViewFilter.None;
            break;
    }
    const tableQuery = createMailFolderTableQuery(
        folderNameToId('inbox'),
        ReactListViewType.Message,
        'mail',
        focusedViewFilter,
        null /* viewFilter */,
        null /* categoryName */,
        null /* sortBy */,
        pivot
    );
    const tableView = getTableViewFromTableQuery(tableQuery, true);
    const mailTableQuery = tableView.tableQuery as MailFolderTableQuery;
    const baseServerFolderId = getBaseServerFolderId(
        tableView,
        true /* returnNullIfSameAsFolderId */
    );
    const hashtagToInclude = getHashtagForInboxClassificationFilter(
        mailTableQuery.inboxClassificationFilter
    );

    // If we already have the searchFolderId in cache, early return
    if (baseServerFolderId) {
        return Promise.resolve((baseServerFolderId as FolderId).Id);
    } else {
        // Throttle the calls to findItem since they're made in a loop
        await new Promise(resolve => setTimeout(resolve, throttleTimeout));

        // We are only interested in the searchFolderId in the response
        return findItemWithStartIndex(
            getBaseFolderId(mailTableQuery),
            0 /* startIndex */,
            1,
            mailTableQuery.viewFilter,
            getMailItemSortByProperty(mailTableQuery),
            mailTableQuery.focusedViewFilter,
            null,
            mailTableQuery.shouldTryToUseSessionDataResponse,
            getMailboxInfoFromTableQuery(mailTableQuery).requestOptions,
            mailTableQuery.categoryName,
            hashtagToInclude.length > 0 ? [hashtagToInclude] : null,
            getHashtagsToExclude(mailTableQuery.inboxClassificationFilter),
            baseServerFolderId
        ).then((findItemResponse: FindItemResponseMessage) => {
            if (findItemResponse.ResponseClass == 'Success') {
                const searchFolderId = findItemResponse.SearchFolderId;

                if (searchFolderId) {
                    // If the tableView is in the cache, we can cache the searchFolderId for future FindItem calls
                    const cachedTableView = listViewStore.tableViews.get(tableView.id);

                    if (cachedTableView) {
                        cachedTableView.serverFolderId = searchFolderId.Id;
                    }
                    return Promise.resolve(searchFolderId.Id);
                }
            }
            return Promise.resolve(null);
        });
    }
}

function getSearchFoldersAndLogCounts(
    searchFolderIds: string[],
    pivotsToLog: InboxClassificationFilter[]
) {
    const searchFolderIdsRequest = searchFolderIds.map(searchFolderId =>
        folderId({ Id: searchFolderId })
    );

    getFolderOperation({
        Header: getJsonRequestHeader(),
        Body: getFolderRequest({
            FolderShape: searchFolderShape(),
            FolderIds: searchFolderIdsRequest,
        }),
    }).then((response: GetFolderJsonResponse) => {
        const responseItems = response.Body.ResponseMessages.Items;

        for (let i = 0; i < responseItems.length; i++) {
            if (responseItems[i].ResponseClass === 'Success') {
                const searchFolder = (responseItems[i] as FolderInfoResponseMessage)
                    .Folders[0] as Folder;
                logUsage(
                    'TotalItemsIn' + getInboxPlusPivotDisplayProperties(pivotsToLog[i]).displayText,
                    [searchFolder.TotalCount, searchFolder.UnreadCount],
                    {
                        isCore: true,
                    }
                );
            }
        }
    });
}

export function searchFolderShape(): FolderResponseShape {
    return folderResponseShape({
        BaseShape: 'IdOnly',
        AdditionalProperties: [
            propertyUri({ FieldURI: 'UnreadCount' }),
            propertyUri({ FieldURI: 'TotalCount' }),
        ],
    });
}
